import React, {useCallback, useEffect, useState} from 'react'
import {convertToHours, formatDateToDDMMYYYY} from '../../common-function/formatDateToYearMonth'
import {KTIcon} from '../../../_metronic/helpers'
import {useNavigate} from 'react-router-dom'
import {restRequest} from '../../../api-utils/apiQueries'
import PaymentFilter from './PaymentFilter'
import Pagination from '../../common-function/Pagination'
import {ExportReportPayload, useExportReport} from '../../common-function/downloadExportFile'
import {
  appendDateFilterToBody,
  appendDateFilterToExport,
} from '../../common-function/adminDateFilterPayload'
import {useAdminListState} from '../../common-function/useAdminListState'
import ListFilterToolbar from '../../common-function/ListFilterToolbar'
import ListPageHeader from '../../common-function/ListPageHeader'
import {
  buildPaymentFilterSummary,
  getDefaultPaymentFilter,
  PaymentFilterState,
} from './paymentDateRange'

/** Prefer API-resolved fields from backend (list + export use the same logic). */
const formatPaymentAmount = (item: any): string => {
  if (item?.display_amount !== undefined && item?.display_amount !== null) {
    return Number(item.display_amount).toFixed(2)
  }
  return '0.00'
}

const LIST_STORAGE_KEY = 'payment-management'

const PaymentManagementWrapper = () => {
  const navigate = useNavigate()
  const {runExport, exporting} = useExportReport()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [payments, setPayments] = useState<any[]>([])
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)

  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  } = useAdminListState<PaymentFilterState>({
    storageKey: LIST_STORAGE_KEY,
    getDefaultFilter: getDefaultPaymentFilter,
    isDefaultFilter: (f, search) =>
      f.preset === 'thisMonth' && !f.transaction_type && !search.trim(),
  })

  const fetchPaymentsData = useCallback(
    async (query: string) => {
      setIsLoading(true)
      const bodyData: Record<string, unknown> = {
        page,
        search: query,
        transaction_type: activeFilter.transaction_type,
        type: '',
      }
      appendDateFilterToBody(bodyData, activeFilter)

      try {
        const response: any = await restRequest.getPaymentsData(bodyData)
        const data = response.data.data
        setPayments(data.data ?? [])
        setTotalPages(data.pager?.totalPages ?? 0)
        setTotalRecords(data.pager?.totalRecords ?? 0)
      } catch (error) {
        console.error('Failed to load payments:', error)
        setPayments([])
        setTotalPages(0)
        setTotalRecords(0)
      } finally {
        setIsLoading(false)
      }
    },
    [page, activeFilter]
  )

  useEffect(() => {
    fetchPaymentsData(searchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value === '') {
      resetPageForSearch()
      fetchPaymentsData('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resetPageForSearch()
      fetchPaymentsData(searchQuery)
    }
  }

  const submitSearch = () => {
    resetPageForSearch()
    fetchPaymentsData(searchQuery)
  }

  const handleExportPayments = () => {
    const payload: ExportReportPayload = {type: 'payment'}
    appendDateFilterToExport(payload, activeFilter)
    if (activeFilter.transaction_type) {
      payload.transaction_type = activeFilter.transaction_type
    }
    if (searchQuery.trim()) payload.search = searchQuery.trim()
    runExport(payload)
  }

  const filterSummary = buildPaymentFilterSummary(
    activeFilter,
    searchQuery,
    totalRecords
  )

  return (
    <div>
      <p className='card-label fw-bold fs-3 mb-2'>Payment Management</p>

      <ListFilterToolbar
        filterSummary={filterSummary}
        showReset={isFilterActive}
        onReset={resetToDefault}
        resetLabel='Reset to this month'
      />

      <div className='card'>
        <ListPageHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchKeyDown={handleKeyDown}
          onSearchSubmit={submitSearch}
          searchPlaceholder='Search payments by user name'
          isLoading={isLoading}
          isFilterActive={isFilterActive}
          onOpenFilter={() => setShowFilter(true)}
          onExport={handleExportPayments}
          exporting={exporting}
        />
        <div className='card-body py-3'>
          {isLoading ? (
            <div className='d-flex justify-content-center py-10'>
              <div className='spinner-border text-primary'></div>
            </div>
          ) : (
            <div className='table-responsive'>
              {payments.length > 0 ? (
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-150px'>User Name</th>
                      <th className='min-w-150px'>Types Of Transaction</th>
                      <th className='min-w-120px'>Amount</th>
                      <th className='min-w-120px'>Payment Mode</th>
                      <th className='min-w-120px'>Time Of Transaction</th>
                      <th className='min-w-120px'>Date</th>
                      <th className='min-w-120px'>Status</th>
                      <th className='min-w-80px text-end'>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((item: any, index: number) => (
                      <tr key={item.id ?? index}>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary fs-6'>
                            {item.user?.name ?? '—'}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary fs-6'>
                            {item.transaction_category ?? '—'}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            ${formatPaymentAmount(item)}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            {item.payment_mode ?? '—'}
                          </p>
                        </td>
                        <td className='text-dark fw-bold text-hover-primary fs-6'>
                          {item.createdAt ? convertToHours(new Date(item.createdAt)) : '—'}
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            {item.createdAt
                              ? formatDateToDDMMYYYY(item.createdAt)
                              : '—'}
                          </p>
                        </td>
                        <td>
                          <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                            <span
                              className={`badge ${
                                item.status === 'Approved'
                                  ? 'badge-light-success'
                                  : item.status === 'Cancel'
                                    ? 'badge-light-danger'
                                    : 'badge-light-warning'
                              } `}
                            >
                              {item.status ?? '—'}
                            </span>
                          </p>
                        </td>
                        <td className='text-end'>
                          {item.has_detail ? (
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              title='View payment details'
                              onClick={() => navigate(`/payment/${item.id}`)}
                            >
                              <KTIcon iconName='eye' className='fs-1' />
                            </button>
                          ) : (
                            <span className='text-muted fs-7'>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className='fs-5 text-center pt-3'>No payments found for this filter.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination totalPages={totalPages} page={page} setPage={setPage} />
      )}

      <PaymentFilter
        showFilter={showFilter}
        toggle={() => setShowFilter(false)}
        activeFilter={activeFilter}
        onApply={applyFilter}
      />
    </div>
  )
}

export default PaymentManagementWrapper
